import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SubtitleLine, FuriganaSegment } from "@/types/subtitle";
import type { Json } from "@/integrations/supabase/types";

// Convert JSON furigana to FuriganaSegment array
// AI generates: {"word": "皆", "reading": "みな"}
// Frontend expects: {"text": "皆", "reading": "みな"}
function parseFurigana(furiganaJson: Json | null): FuriganaSegment[] | undefined {
  if (!furiganaJson) return undefined;

  const normalizeOne = (item: unknown): FuriganaSegment | null => {
    if (typeof item === "object" && item !== null) {
      const obj = item as Record<string, unknown>;
      // Handle multiple source formats.
      const text = String(
        obj.word ??
          obj.text ??
          obj.surface_form ??
          obj.surface ??
          ""
      ).trim();
      const readingRaw = obj.reading ?? obj.kana ?? obj.pronunciation;
      const reading = readingRaw ? String(readingRaw).trim() : undefined;
      if (!text) return null;
      return { text, reading };
    }
    const text = String(item ?? "").trim();
    if (!text) return null;
    return { text };
  };

  const normalizeMany = (value: unknown): FuriganaSegment[] | undefined => {
    if (Array.isArray(value)) {
      const mapped = value
        .map((item) => normalizeOne(item))
        .filter((item): item is FuriganaSegment => !!item);
      return mapped.length > 0 ? mapped : undefined;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return undefined;
      try {
        return normalizeMany(JSON.parse(trimmed));
      } catch {
        const single = normalizeOne(trimmed);
        return single ? [single] : undefined;
      }
    }

    if (typeof value === "object" && value !== null) {
      // Some data sources wrap furigana under nested keys.
      const wrapped = value as Record<string, unknown>;
      const nested = wrapped.segments ?? wrapped.furigana ?? wrapped.data;
      if (nested !== undefined) return normalizeMany(nested);
      const single = normalizeOne(value);
      return single ? [single] : undefined;
    }

    return undefined;
  };

  return normalizeMany(furiganaJson);
}

// Map database subtitle to frontend SubtitleLine type
type RawSubtitleRow = {
  id: string;
  start_time: number;
  end_time: number;
  japanese: string;
  chinese: string;
  furigana: Json | null;
  romaji: string | null;
  sort_order: number | null;
};

function mapDbSubtitleToSubtitleLine(dbSubtitle: RawSubtitleRow): SubtitleLine {
  return {
    id: dbSubtitle.id,
    startTime: Number(dbSubtitle.start_time),
    endTime: Number(dbSubtitle.end_time),
    japanese: dbSubtitle.japanese,
    chinese: dbSubtitle.chinese,
    furigana: parseFurigana(dbSubtitle.furigana),
    romaji: dbSubtitle.romaji || undefined,
  };
}

async function fetchVideoSubtitles(videoId: string): Promise<RawSubtitleRow[]> {
  const { data, error } = await supabase
    .from("subtitles")
    .select("*")
    .eq("video_id", videoId)
    .order("sort_order", { ascending: true });

  if (error) throw error;
  return (data || []) as RawSubtitleRow[];
}

async function fetchAudioSubtitles(audioId: string): Promise<RawSubtitleRow[]> {
  const { data, error } = await supabase
    .from("audio_subtitles")
    .select("*")
    .eq("audio_id", audioId)
    .order("sort_order", { ascending: true });

  if (error) throw error;
  return (data || []) as RawSubtitleRow[];
}

function isMissingRelationError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const maybe = error as { message?: unknown; details?: unknown };
  const message = typeof maybe.message === "string" ? maybe.message.toLowerCase() : "";
  const details = typeof maybe.details === "string" ? maybe.details.toLowerCase() : "";
  return message.includes("does not exist") || details.includes("does not exist");
}

export function useSubtitles(videoId: string, source: "video" | "audio" | "auto" = "auto") {
  return useQuery({
    queryKey: ["subtitles", source, videoId],
    queryFn: async () => {
      try {
        if (source === "video") {
          const rows = await fetchVideoSubtitles(videoId);
          return rows.map(mapDbSubtitleToSubtitleLine);
        }

        if (source === "audio") {
          let audioRows: RawSubtitleRow[] = [];
          try {
            audioRows = await fetchAudioSubtitles(videoId);
          } catch (error) {
            if (!isMissingRelationError(error)) throw error;
            // Compatibility for instances that haven't created audio_subtitles yet.
            console.warn("audio_subtitles table is missing, fallback to subtitles table");
          }

          if (audioRows.length > 0) {
            return audioRows.map(mapDbSubtitleToSubtitleLine);
          }

          // Fallback for legacy records that are tagged as audio in UI
          // but still store subtitles in the video subtitles table.
          const videoRows = await fetchVideoSubtitles(videoId);
          return videoRows.map(mapDbSubtitleToSubtitleLine);
        }

        // Auto mode:
        // 1) Try video subtitles first
        // 2) Fallback to audio subtitles for audio content routed through /player/:videoId
        const videoRows = await fetchVideoSubtitles(videoId);
        if (videoRows.length > 0) {
          return videoRows.map(mapDbSubtitleToSubtitleLine);
        }

        let audioRows: RawSubtitleRow[] = [];
        try {
          audioRows = await fetchAudioSubtitles(videoId);
        } catch (error) {
          if (!isMissingRelationError(error)) throw error;
          console.warn("audio_subtitles table is missing, skip audio subtitle fallback");
        }
        return audioRows.map(mapDbSubtitleToSubtitleLine);
      } catch (error) {
        console.error("Error fetching subtitles:", error);
        throw error;
      }
    },
    enabled: !!videoId,
  });
}
